/*
 * The preview's clients, jobs and visits (draft 2): made-up records and the routes that answer them, with the same shapes,
 * permission checks and refusals as the real server. Visits are planned from jobs with the shared rules (planVisits,
 * linesOn), and search uses the same matching as the server (searchMatches). Nothing is saved; a reload starts over.
 */
import {
  addDays,
  oneLine as partsLine,
  splitAddress,
  type AddressParts,
  lastActivity,
  riskWords,
  serviceCategory,
  BULK_LIMIT,
  bulkClientsBody,
  checklistInput,
  checklistTickBody,
  clientInput,
  clientName,
  clientPatch,
  computedRef,
  contactInput,
  daysBetween,
  deactivateBody,
  fileUpload,
  repeatWords,
  isDue,
  jobInput,
  jobPatch,
  linesOn,
  mergeBody,
  noteInput,
  notePatch,
  parseVisitRef,
  planVisits,
  propertyInput,
  propertyPatch,
  reminderInput,
  reminderPatch,
  paymentTermsWords,
  propertySummary,
  searchMatches,
  shortName,
  visitCompleteBody,
  visitCrewBody,
  visitLinesBody,
  visitScheduleBody,
  visitFutureBody,
  visitStatusBody,
  visitTimerBody,
  type Action,
  type Cents,
  type ClientDetail,
  type ActivityKind,
  type ClientRow,
  type ContactEdit,
  type ContactType,
  type FileRow,
  type HomeAnswer,
  type HomeAnswers,
  type JobDetail,
  type JobRow,
  type LineRow,
  type MyVisit,
  type MyVisitDetail,
  type NoteRow,
  type PersonChip,
  type PlannedVisit,
  type PricingRow,
  type ReminderRow,
  type Role,
  type RouteRow,
  type VisitCard,
  type VisitDetail,
  type DisputeFile,
  policiesToSign,
  SETTINGS_DEFAULTS,
  jobExpenseInput,
  jobExpensePatch,
  jobProfit,
  type JobCosts,
  type JobExpenseRow,
  type JobLaborRow,
  type JobTime,
} from "@steward/shared";
import type { z } from "zod";
import { demoThreadFor } from "./field";

type Answer = Promise<{ status: number; body: unknown }>;
const ok = (body: unknown, status = 200): Answer => Promise.resolve({ status, body });
const fail = (status: number, code: string, message: string, extra: Record<string, unknown> = {}): Answer =>
  Promise.resolve({ status, body: { code, message, ...extra } });
const NOT_ALLOWED = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");
const NOT_FOUND = (what: string) => fail(404, "not_found", `That ${what} was not found.`);
const bad = (e: z.ZodError) => fail(400, "bad_request", e.issues[0]?.message ?? "Something in the request is not right.");

/** The people the work hangs on, as the preview server keeps them. */
export interface WorkPerson {
  id: string;
  companyId: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  role: string;
  seesPrices: boolean;
  active: boolean;
  locale: string;
  color?: string | null;
  photo?: string | null;
}

export interface WorkCtx {
  companyId: string;
  me: { id: string; name: string };
  role: Role;
  gate: (a: Action) => boolean;
  people: WorkPerson[];
  log: (action: string) => void;
}

interface Client {
  companyId: string;
  id: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  tags: string[];
  leadSource: string | null;
  prefers: "text" | "email";
  noMarketing: boolean;
  notes: string | null;
  officeNotes: string | null;
  active: boolean;
  inactiveReason: ClientRow["inactiveReason"];
  inactiveNote: string | null;
  inactiveAt: string | null;
  mergedIntoId: string | null;
  createdAt: string;
  billingAddress?: string | null;
  billing?: AddressParts | null;
  referredById?: string | null;
  /** A business with no company name. */
  isCompany?: boolean;
}
interface Contact {
  companyId: string;
  clientId: string;
  id: string;
  kind: "phone" | "email";
  value: string;
  type?: ContactType | null;
  label: string | null;
  isMain: boolean;
  optedOut: string | null;
}
interface Property {
  companyId: string;
  clientId: string;
  id: string;
  label: string | null;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  beds: number | null;
  baths: number | null;
  rooms: number | null;
  sqft: number | null;
  lawnSqft: number | null;
  accessNotes: string | null;
  notes: string | null;
}
interface Line {
  id: string;
  description: string;
  note: string | null;
  quantity: number;
  unitPriceCents: number;
  amountCents: Cents;
  frequency: string;
  startDate: string | null;
  endsOn: string | null;
  /** Job lines priced from the price list: the answers it was priced from. */
  answers?: HomeAnswers | null;
}
interface Job {
  companyId: string;
  id: string;
  number: number;
  clientId: string;
  propertyId: string | null;
  title: string | null;
  service: string | null;
  frequency: string;
  startDate: string;
  repeatDay?: number | null;
  startTime: string | null;
  arriveFrom: string | null;
  arriveTo: string | null;
  hours: number | null;
  endsOn: string | null;
  crew: string[];
  billing: string;
  officeNotes: string | null;
  crewNotes: string | null;
  crewNotesEs: string | null;
  checklistId: string | null;
  skipDates: string[];
  active: boolean;
  lines: Line[];
  /** The route it runs on (lawn) and its place on it. */
  routeId: string | null;
  routePosition: number | null;
  autoPay?: boolean;
}
interface Visit {
  companyId: string;
  id: string;
  jobId: string;
  originalDate: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  status: VisitCard["status"];
  crew: string[] | null;
  lines: Line[] | null;
  timerStartedAt: string | null;
  timerMinutes: number;
  /** Time taken off in the job's time log, kept for Undo (demo only). */
  removedMinutes?: number;
  completedAt: string | null;
  completedBy: string | null;
  crewCollected: "cash" | "check" | "company" | null;
  checks: Record<string, string | null>;
}
interface Note {
  companyId: string;
  id: string;
  on: "clients" | "jobs" | "visits";
  subjectId: string;
  body: string;
  visibility: "office" | "crew";
  pinned: boolean;
  authorName: string | null;
  createdAt: string;
  deleted: boolean;
}
interface StoredFile extends FileRow {
  companyId: string;
  on: "clients" | "visits";
  subjectId: string;
  deleted: boolean;
}
interface Checklist {
  companyId: string;
  id: string;
  name: string;
  items: { id: string; label: string; labelEs: string | null; room?: string | null }[];
}

const clients: Client[] = [];
const contacts: Contact[] = [];
const properties: Property[] = [];
const jobs: Job[] = [];
const visits: Visit[] = [];
const jobExpenses: (JobExpenseRow & { companyId: string; jobId: string; gone: boolean })[] = [];
const notes: Note[] = [];
const files: StoredFile[] = [];
/** The office's own reminders on a client (Client schedule). */
const reminders: (ReminderRow & { clientId: string; companyId: string })[] = [];
const checklists: Checklist[] = [];
const jobNumbers = new Map<string, number>();
let newId: () => string = () => crypto.randomUUID();
let TODAY = "";
let allPeople: WorkPerson[] = [];

const daysAgo = (d: number) => new Date(Date.now() - d * 864e5).toISOString();
const amount = (quantity: number, unit: number) => Math.round(quantity * unit) as Cents;

// Routes (lawn) --------------------------------------------------------------------------------------------------------

export interface DemoRoute {
  companyId: string;
  id: string;
  name: string;
  weekday: number;
  crew: string[];
  color: string | null;
  active: boolean;
}
export const demoRoutes: DemoRoute[] = [];

/** A company's routes with their jobs in order, as GET routes answers. */
export function routeRows(companyId: string): RouteRow[] {
  return demoRoutes
    .filter((r) => r.companyId === companyId)
    .sort((a, b) => a.weekday - b.weekday || a.name.localeCompare(b.name))
    .map(({ companyId: _c, ...r }) => ({
      ...r,
      week: null,
      jobs: jobs
        .filter((j) => j.routeId === r.id)
        .sort((a, b) => (a.routePosition ?? 0) - (b.routePosition ?? 0))
        .map((j, i) => {
          const prop = propertyOf(j);
          return {
            id: j.id,
            name: jobName(j),
            clientName: nameOf(clientOf(j.clientId)),
            address: prop ? oneLine(prop) : null,
            position: i,
          };
        }),
    }));
}

/** Puts these jobs on the route in this order; jobs left out come off it. */
export function setRouteJobs(routeId: string, jobIds: readonly string[]): void {
  for (const j of jobs)
    if (j.routeId === routeId) {
      j.routeId = null;
      j.routePosition = null;
    }
  jobIds.forEach((jid, i) => {
    const j = jobs.find((x) => x.id === jid);
    if (j) Object.assign(j, { routeId, routePosition: i });
  });
}

// Seed ---------------------------------------------------------------------------------------------------------------

interface SeedClient {
  first?: string;
  last?: string;
  company?: string;
  phone: string;
  email?: string;
  tags?: string[];
  source?: string;
  address?: string;
  city?: string;
  zip?: string;
  beds?: number;
  baths?: number;
  rooms?: number;
  sqft?: number;
  lawnSqft?: number;
  access?: string;
  created: number;
  inactive?: Client["inactiveReason"];
}
interface SeedJob {
  title?: string;
  service: string;
  frequency: string;
  start: number;
  time: string | null;
  hours: number;
  crew: string[];
  lines: [string, number, string?][];
  crewNotes?: string;
  crewNotesEs?: string;
  officeNotes?: string;
  checklistId?: string;
  routeId?: string;
  routePosition?: number;
  ended?: number;
}

function addClient(companyId: string, s: SeedClient): Client {
  const c: Client = {
    companyId,
    id: newId(),
    firstName: s.first ?? null,
    lastName: s.last ?? null,
    companyName: s.company ?? null,
    phone: s.phone,
    email: s.email ?? null,
    tags: s.tags ?? [],
    leadSource: s.source ?? null,
    prefers: "text",
    noMarketing: false,
    notes: null,
    officeNotes: null,
    active: !s.inactive,
    inactiveReason: s.inactive ?? null,
    inactiveNote: null,
    inactiveAt: s.inactive ? daysAgo(40) : null,
    mergedIntoId: null,
    createdAt: daysAgo(s.created),
  };
  clients.push(c);
  contacts.push({
    companyId,
    clientId: c.id,
    id: newId(),
    kind: "phone",
    value: s.phone,
    label: "Mobile",
    isMain: true,
    optedOut: null,
  });
  if (s.email)
    contacts.push({
      companyId,
      clientId: c.id,
      id: newId(),
      kind: "email",
      value: s.email,
      label: null,
      isMain: true,
      optedOut: null,
    });
  if (s.address)
    properties.push({
      companyId,
      clientId: c.id,
      id: newId(),
      label: null,
      address: s.address,
      city: s.city ?? "New Orleans",
      state: "LA",
      zip: s.zip ?? "70115",
      beds: s.beds ?? null,
      baths: s.baths ?? null,
      rooms: s.rooms ?? null,
      sqft: s.sqft ?? null,
      lawnSqft: s.lawnSqft ?? null,
      accessNotes: s.access ?? null,
      notes: null,
    });
  return c;
}

function nextNumber(companyId: string): number {
  const n = (jobNumbers.get(companyId) ?? 100) + 1;
  jobNumbers.set(companyId, n);
  return n;
}

function addJob(client: Client, s: SeedJob): Job {
  const j: Job = {
    companyId: client.companyId,
    id: newId(),
    number: nextNumber(client.companyId),
    clientId: client.id,
    propertyId: properties.find((p) => p.clientId === client.id)?.id ?? null,
    title: s.title ?? null,
    service: s.service,
    frequency: s.frequency,
    startDate: addDays(TODAY, s.start),
    startTime: s.time,
    arriveFrom: null,
    arriveTo: null,
    hours: s.hours,
    endsOn: s.ended !== undefined ? addDays(TODAY, s.ended) : null,
    crew: s.crew,
    billing: "visit",
    officeNotes: s.officeNotes ?? null,
    crewNotes: s.crewNotes ?? null,
    crewNotesEs: s.crewNotesEs ?? null,
    checklistId: s.checklistId ?? null,
    skipDates: [],
    routeId: s.routeId ?? null,
    routePosition: s.routePosition ?? null,
    active: s.ended === undefined,
    lines: s.lines.map(([description, price, frequency]) => ({
      id: newId(),
      description,
      note: null,
      quantity: 1,
      unitPriceCents: price,
      amountCents: price as Cents,
      frequency: frequency ?? "every",
      startDate: null,
      endsOn: null,
    })),
  };
  jobs.push(j);
  return j;
}

function touchRow(j: Job, date: string, extra: Partial<Visit> = {}): Visit {
  const v: Visit = {
    companyId: j.companyId,
    id: newId(),
    jobId: j.id,
    originalDate: date,
    date,
    startTime: j.startTime,
    endTime: null,
    status: "scheduled",
    crew: null,
    lines: null,
    timerStartedAt: null,
    timerMinutes: 0,
    completedAt: null,
    completedBy: null,
    crewCollected: null,
    checks: {},
    ...extra,
  };
  visits.push(v);
  return v;
}

/** Fills the preview with clients, jobs and visits around today, for the three New Orleans locations. */
export function seedWork(opts: {
  id: () => string;
  today: string;
  people: WorkPerson[];
  cleaners: string;
  lawn: string;
  washers: string;
}): void {
  newId = opts.id;
  TODAY = opts.today;
  allPeople = opts.people;
  const crewOf = (cid: string) => opts.people.filter((p) => p.companyId === cid && p.role === "crew" && p.active);
  const [kenia, kevin, rosa] = crewOf(opts.cleaners).map((p) => p.id);
  const [luis, jake] = crewOf(opts.lawn).map((p) => p.id);
  const k = kenia ?? "";
  const v = kevin ?? "";
  const r = rosa ?? "";

  const house: Checklist = {
    companyId: opts.cleaners,
    id: newId(),
    name: "House cleaning",
    items: [
      ["Kitchen counters and sink", "Mostradores y fregadero de la cocina"],
      ["Bathrooms clean and dry", "Baños limpios y secos"],
      ["Vacuum and mop floors", "Aspirar y trapear los pisos"],
      ["Beds made", "Camas tendidas"],
      ["Trash out", "Sacar la basura"],
    ].map(([label, labelEs]) => ({ id: newId(), label: label ?? "", labelEs: labelEs ?? null })),
  };
  const office: Checklist = {
    companyId: opts.cleaners,
    id: newId(),
    name: "Office cleaning",
    items: [
      ["Empty every trash can", "Vaciar todos los botes de basura"],
      ["Wipe desks and door handles", "Limpiar escritorios y manijas"],
      ["Restrooms stocked", "Baños con papel y jabón"],
    ].map(([label, labelEs]) => ({ id: newId(), label: label ?? "", labelEs: labelEs ?? null })),
  };
  checklists.push(house, office);

  const C = (s: SeedClient) => addClient(opts.cleaners, s);
  const sam = C({
    first: "Sam",
    last: "Rivers",
    phone: "(504) 555-0170",
    email: "sam.rivers@example.com",
    tags: ["Weekly", "Dog"],
    source: "google",
    address: "100 Example St",
    beds: 3,
    baths: 2,
    sqft: 1600,
    access: "Gate code 4512. Alarm pad is by the back door.",
    created: 400,
  });
  sam.officeNotes = "Pays by card on file. Likes a text the night before.";
  const samJob = addJob(sam, {
    title: "Weekly House Cleaning",
    service: "House Cleaning",
    frequency: "weekly",
    start: -21,
    time: "09:00",
    hours: 3,
    crew: [k, v],
    lines: [
      ["House cleaning", 14000],
      ["Inside windows", 4000, "monthly"],
    ],
    crewNotes: "Dog is friendly. Start upstairs and work down.",
    crewNotesEs: "El perro es amigable. Empieza arriba y baja.",
    officeNotes: "Card on file. Raise to $150 in January.",
    checklistId: house.id,
  });
  const samuel = C({
    first: "Samuel",
    last: "Rivers",
    phone: "504-555-0170",
    source: "website",
    address: "100 Example St",
    created: 20,
  });
  // Priced from the price list: 3 bed, 2 bath and an office is 8 rooms at $32 (the job page shows the math under the line).
  const deep = addJob(samuel, {
    service: "Deep House Cleaning",
    frequency: "one-time",
    start: -10,
    time: "10:00",
    hours: 4,
    crew: [k],
    lines: [["Deep Cleaning, 3 bed, 2 bath, 1 more room", 25600]],
  });
  deep.lines[0]!.answers = { serviceId: "s2", sizeBy: "rooms", beds: 3, baths: 2, other: 1, sqft: 0, often: "once", extras: {} };
  const ana = C({
    first: "Ana",
    last: "Bell",
    phone: "(504) 555-0171",
    email: "ana.bell@example.com",
    source: "referral",
    address: "200 Sample Ave",
    city: "Metairie",
    zip: "70001",
    beds: 4,
    baths: 3,
    sqft: 2400,
    created: 12,
    tags: ["New"],
  });
  addJob(ana, {
    service: "Deep House Cleaning",
    frequency: "one-time",
    start: 1,
    time: "13:00",
    hours: 4,
    crew: [k],
    lines: [
      ["Deep cleaning", 32500],
      ["Inside the oven", 4500],
    ],
    crewNotes: "Start with the kitchen. Extra time for the oven.",
    crewNotesEs: "Empieza con la cocina. Más tiempo para el horno.",
    checklistId: house.id,
  });
  const dental = C({
    first: "Jo",
    last: "Pratt",
    company: "Example Dental LLC",
    phone: "(504) 555-0172",
    email: "office@example.com",
    tags: ["Office"],
    source: "lsa",
    address: "300 Test Blvd",
    rooms: 8,
    sqft: 3000,
    created: 300,
  });
  addJob(dental, {
    title: "Office Cleaning",
    service: "Office Cleaning",
    frequency: "biweekly",
    start: -14,
    time: "18:00",
    hours: 2.5,
    crew: [v],
    lines: [["Office cleaning", 18000]],
    checklistId: office.id,
  });
  const jo = C({
    first: "Jo",
    last: "Martin",
    phone: "(504) 555-0173",
    tags: ["Weekly"],
    source: "google",
    address: "410 Sample St",
    beds: 2,
    baths: 1,
    sqft: 1100,
    created: 220,
  });
  addJob(jo, {
    service: "House Cleaning",
    frequency: "weekly",
    start: -19,
    time: "10:00",
    hours: 2,
    crew: [r],
    lines: [["House cleaning", 12000]],
    checklistId: house.id,
  });
  const priya = C({
    first: "Priya",
    last: "Shah",
    phone: "(504) 555-0174",
    email: "priya@example.com",
    source: "facebook",
    address: "52 Test Ct",
    beds: 3,
    baths: 2,
    created: 150,
  });
  addJob(priya, {
    service: "House Cleaning",
    frequency: "biweekly",
    start: -8,
    time: "13:00",
    hours: 2.5,
    crew: [k],
    lines: [["House cleaning", 15000]],
  });
  const marcus = C({
    first: "Marcus",
    last: "Lee",
    phone: "(504) 555-0175",
    source: "yard_sign",
    address: "77 Demo Pl",
    beds: 4,
    baths: 2,
    created: 90,
  });
  addJob(marcus, {
    service: "House Cleaning",
    frequency: "monthly",
    start: -25,
    time: "11:00",
    hours: 3,
    crew: [],
    lines: [["House cleaning", 20000]],
  });
  const grace = C({ first: "Grace", last: "Kim", phone: "(504) 555-0176", address: "9 Mock Way", beds: 2, created: 14 });
  addJob(grace, {
    service: "Move Out Cleaning",
    frequency: "one-time",
    start: -6,
    time: "09:30",
    hours: 5,
    crew: [k, r],
    lines: [["Move out cleaning", 28000]],
  });
  const tom = C({
    first: "Tom",
    last: "Baker",
    phone: "(504) 555-0177",
    address: "18 Example Rd",
    created: 380,
    inactive: "moved",
  });
  addJob(tom, {
    service: "House Cleaning",
    frequency: "weekly",
    start: -200,
    time: "09:00",
    hours: 2,
    crew: [v],
    lines: [["House cleaning", 11000]],
    ended: -40,
  });
  C({
    first: "Lena",
    last: "Ortiz",
    phone: "(504) 555-0178",
    email: "lena@example.com",
    source: "facebook",
    tags: ["New"],
    created: 3,
  });
  const bayou = C({
    company: "Bayou Coffee Co",
    first: "Dee",
    last: "Rowe",
    phone: "(504) 555-0179",
    tags: ["Office"],
    source: "referral",
    address: "600 Sample Blvd",
    rooms: 4,
    created: 120,
  });
  addJob(bayou, {
    service: "Office Cleaning",
    frequency: "weekly",
    start: -16,
    time: "19:00",
    hours: 1.5,
    crew: [v],
    lines: [["Office cleaning", 9500]],
    checklistId: office.id,
  });
  const nia = C({
    first: "Nia",
    last: "Brooks",
    phone: "(504) 555-0180",
    address: "25 Test Ave",
    beds: 3,
    baths: 2,
    sqft: 1500,
    created: 200,
  });
  const niaJob = addJob(nia, {
    service: "House Cleaning",
    frequency: "biweekly",
    start: -17,
    time: "08:30",
    hours: 3,
    crew: [r, k],
    lines: [["House cleaning", 16000]],
  });
  C({ first: "David", last: "Chen", phone: "(504) 555-0181", source: "google", address: "31 Demo St", created: 40 });

  const more = [
    ["Ruth", "Allen"],
    ["Omar", "Haddad"],
    ["Carla", "Nunez"],
    ["Ben", "Ford"],
    ["Ivy", "Tran"],
    ["Leo", "Marsh"],
    ["Mia", "Lopez"],
    ["Noah", "Price"],
    ["Zoe", "Patel"],
    ["Eli", "Ward"],
    ["Rita", "Moss"],
    ["Sean", "Doyle"],
  ] as const;
  const crewTurns = [[k], [v], [r], [], [k, r], [v]];
  const times = ["08:00", "10:30", "13:00", "15:00"];
  more.forEach(([first, last], i) => {
    const cl = C({
      first,
      last,
      phone: `(504) 555-${String(190 + i).padStart(4, "0")}`,
      address: `${String(120 + i * 7)} Sample St`,
      beds: 2 + (i % 3),
      baths: 1 + (i % 2),
      source: i % 2 ? "google" : "referral",
      created: 30 + i * 11,
    });
    addJob(cl, {
      service: "House Cleaning",
      frequency: i % 3 === 0 ? "monthly" : "biweekly",
      start: -(i * 3 + 2),
      time: times[i % times.length] ?? null,
      hours: 2,
      crew: crewTurns[i % crewTurns.length] ?? [],
      lines: [["House cleaning", 11000 + (i % 4) * 1500]],
    });
  });

  addNoteSeed(opts.cleaners, "clients", sam.id, "Pays by card on file. Likes a text the night before.", "office", true, 30);
  addNoteSeed(opts.cleaners, "clients", sam.id, "Shoes off inside, please.", "crew", false, 9);
  addNoteSeed(opts.cleaners, "jobs", samJob.id, "Raise to $150 starting in January. Told her on the phone.", "office", false, 4);

  // Past visits are done, except one left open last week (it shows on Home as needing you).
  for (const j of jobs.filter((x) => x.companyId === opts.cleaners)) {
    for (const pv of planVisits(j, [], addDays(TODAY, -28), addDays(TODAY, -1))) {
      if (j === niaJob && pv.date === addDays(TODAY, -3)) continue;
      const by = allPeople.find((p) => p.id === j.crew[0]);
      touchRow(j, pv.date, {
        status: "done",
        completedAt: `${pv.date}T21:00:00.000Z`,
        completedBy: by ? shortName(by.firstName, by.lastName) : "Dana R.",
        // One timer on Sam's job was left on overnight, so the job's time log shows Looks long.
        timerMinutes:
          j === samJob && pv.date >= addDays(TODAY, -14) && pv.date < addDays(TODAY, -7)
            ? 9 * 60 + 40
            : Math.round((j.hours ?? 2) * 60) - 10 + (pv.date.charCodeAt(9) % 4) * 5,
      });
    }
  }
  // One visit moved a day this week, so the schedule shows a moved visit.
  const samNext = planVisits(samJob, [], addDays(TODAY, 1), addDays(TODAY, 7))[0];
  if (samNext) touchRow(samJob, samNext.date, { date: addDays(samNext.date, 1) });

  const L = (s: SeedClient) => addClient(opts.lawn, s);
  const lee = L({ first: "Lee", last: "Park", phone: "(504) 555-0182", address: "400 Demo Dr", lawnSqft: 6000, created: 80 });
  addJob(lee, {
    title: "Weekly Lawn Care",
    service: "Lawn Mowing",
    frequency: "weekly",
    start: -20,
    time: "08:00",
    hours: 1,
    crew: [luis ?? "", jake ?? ""].filter(Boolean),
    lines: [
      ["Mow, edge and blow", 5500],
      ["Hedge trim", 4000, "monthly"],
    ],
  });
  const carmen = L({
    first: "Carmen",
    last: "Diaz",
    phone: "(504) 555-0183",
    address: "14 Mock St",
    lawnSqft: 4200,
    created: 25,
  });
  addJob(carmen, {
    service: "Lawn Mowing",
    frequency: "biweekly",
    start: -5,
    time: "10:00",
    hours: 1,
    crew: [],
    lines: [["Mow, edge and blow", 6500]],
  });
  // Two lawn routes: Broadmoor runs today, Westbank in two days, each with its stops in order.
  const wd = (d: string) => ((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7) + 1;
  const broadmoor: DemoRoute = {
    companyId: opts.lawn,
    id: newId(),
    name: "Broadmoor",
    weekday: wd(TODAY),
    crew: [luis ?? "", jake ?? ""].filter(Boolean),
    color: "#367C2B",
    active: true,
  };
  const westbank: DemoRoute = {
    companyId: opts.lawn,
    id: newId(),
    name: "Westbank",
    weekday: wd(addDays(TODAY, 2)),
    crew: [luis ?? ""].filter(Boolean),
    color: "#0A84FF",
    active: true,
  };
  demoRoutes.push(broadmoor, westbank);
  const stops: [string, string, string, number, DemoRoute, number][] = [
    ["Ana", "Ruiz", "4100 Napoleon Ave", 4500, broadmoor, -14],
    ["Tom", "Gray", "3800 S Galvez St", 5000, broadmoor, -21],
    ["Ida", "Bell", "4400 S Tonti St", 4000, broadmoor, -7],
    ["Raul", "Soto", "3900 Fontainebleau Dr", 5500, broadmoor, -28],
    ["Joy", "Hart", "4300 Walmsley Ave", 4500, broadmoor, -14],
    ["Nate", "Cole", "700 Lapalco Blvd", 6000, westbank, -12],
    ["Vera", "Lin", "1200 Manhattan Blvd", 5000, westbank, -5],
    ["Ed", "Moss", "2100 Barataria Blvd", 4500, westbank, -19],
  ];
  stops.forEach(([first, last, address, price, route, start], i) =>
    addJob(L({ first, last, phone: `(504) 555-02${String(10 + i)}`, address, lawnSqft: 5000, created: 40 + i }), {
      title: "Weekly Lawn Care",
      service: "Lawn Mowing",
      frequency: "weekly",
      start,
      time: null,
      hours: 1,
      crew: [],
      lines: [["Mow, edge and blow", price]],
      routeId: route.id,
      routePosition: i,
    }),
  );
  const W = (s: SeedClient) => addClient(opts.washers, s);
  const river = W({
    company: "Riverbend Apartments",
    first: "Kim",
    last: "Vo",
    phone: "(504) 555-0184",
    address: "500 Mock Ln",
    city: "Kenner",
    zip: "70062",
    created: 30,
  });
  addJob(river, {
    service: "House Wash",
    frequency: "one-time",
    start: 2,
    time: "10:00",
    hours: 6,
    crew: [],
    lines: [["Building wash", 45000]],
  });
  const pat = W({ first: "Pat", last: "Quinn", phone: "(504) 555-0185", address: "8 Test Ln", created: 6 });
  addJob(pat, {
    service: "Driveway Wash",
    frequency: "one-time",
    start: 4,
    time: "09:00",
    hours: 2,
    crew: [],
    lines: [["Driveway and walk", 18000]],
  });
}

function addNoteSeed(
  companyId: string,
  on: Note["on"],
  subjectId: string,
  body: string,
  visibility: Note["visibility"],
  pinned: boolean,
  age: number,
) {
  notes.push({
    companyId,
    id: newId(),
    on,
    subjectId,
    body,
    visibility,
    pinned,
    authorName: "Dana R.",
    createdAt: daysAgo(age),
    deleted: false,
  });
}

// Shapes -------------------------------------------------------------------------------------------------------------

const personOf = (pid: string) => allPeople.find((p) => p.id === pid);
const chip = (pid: string): PersonChip | null => {
  const p = personOf(pid);
  return p ? { id: p.id, name: shortName(p.firstName, p.lastName), color: p.color ?? null, photo: p.photo ?? null } : null;
};
const chips = (ids: string[]) => ids.map(chip).filter((x): x is PersonChip => x !== null);
const lineRow = (l: Line): LineRow => ({
  id: l.id,
  description: l.description,
  note: l.note,
  quantity: l.quantity,
  unitPriceCents: l.unitPriceCents,
  amountCents: l.amountCents,
  frequency: l.frequency,
  ...(l.answers !== undefined ? { answers: l.answers } : {}),
});
const oneLine = (p: Property) => [p.address, p.city, [p.state, p.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
/** A made-up map spot in New Orleans for a property, the same every time for the same address. */
function mapSpot(p: Property): { lat: number; lng: number } {
  let h = 0;
  for (const ch of p.address) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { lat: 29.935 + ((h % 1000) / 1000) * 0.05, lng: -90.12 + (((h >>> 10) % 1000) / 1000) * 0.08 };
}
const mainProperty = (clientId: string) => properties.find((p) => p.clientId === clientId);
const clientOf = (id: string) => clients.find((c) => c.id === id);
const nameOf = (c: Client | undefined) => (c ? clientName(c) : "Client");
const mineClientAny = (id: string) => clients.find((c) => c.id === id);
const jobName = (j: Job) => j.title ?? j.service ?? "Job";
const propertyOf = (j: Job) => properties.find((p) => p.id === j.propertyId) ?? mainProperty(j.clientId);
const noteRow = (n: Note): NoteRow => ({
  id: n.id,
  body: n.body,
  visibility: n.visibility,
  pinned: n.pinned,
  authorName: n.authorName,
  createdAt: n.createdAt,
});
const notesOn = (on: Note["on"], id: string) =>
  notes
    .filter((n) => n.on === on && n.subjectId === id && !n.deleted)
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt.localeCompare(a.createdAt))
    .map(noteRow);
const filesOn = (on: StoredFile["on"], id: string): FileRow[] =>
  files
    .filter((f) => f.on === on && f.subjectId === id && !f.deleted)
    .map(({ id: fid, name, mime, size, kind, createdAt }) => ({ id: fid, name, mime, size, kind, createdAt }));

function plusHours(hhmm: string | null, hours: number | null): string | null {
  if (!hhmm || !hours) return null;
  const [h = "0", m = "0"] = hhmm.split(":");
  const t = Math.min(23 * 60 + 59, Number(h) * 60 + Number(m) + Math.round(hours * 60));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}
const jobLines = (j: Job, date: string) => linesOn(j.lines, j.startDate, date, j.repeatDay ?? null);

interface Found {
  job: Job;
  pv: PlannedVisit;
  row: Visit | undefined;
}

function card({ job, pv, row }: Found): VisitCard {
  const client = clientOf(job.clientId);
  const lines = row?.lines ?? jobLines(job, pv.originalDate);
  const prop = propertyOf(job);
  const start = row ? row.startTime : job.startTime;
  return {
    ref: pv.ref,
    id: pv.id,
    jobId: job.id,
    jobNumber: job.number,
    jobName: jobName(job),
    clientId: job.clientId,
    clientName: nameOf(client),
    date: pv.date,
    originalDate: pv.originalDate,
    moved: pv.moved,
    startTime: start,
    endTime: row?.endTime ?? plusHours(start, job.hours),
    status: row?.status ?? "scheduled",
    address: prop ? oneLine(prop) : null,
    ...(prop ? mapSpot(prop) : { lat: null, lng: null }),
    // Route people work its stops unless the job or visit has its own.
    crew: chips(row?.crew ?? (job.crew.length ? job.crew : (demoRoutes.find((r) => r.id === job.routeId)?.crew ?? []))),
    totalCents: lines.reduce((s, l) => s + l.amountCents, 0),
    oneTime: job.frequency === "one-time",
    commercial: Boolean(client?.companyName),
    routeId: job.routeId,
  };
}

function detail(f: Found): VisitDetail {
  const { job, row } = f;
  const client = clientOf(job.clientId);
  const prop = propertyOf(job);
  const list = checklists.find((c) => c.id === job.checklistId);
  const phone = contacts.find((x) => x.clientId === job.clientId && x.kind === "phone" && x.isMain)?.value ?? null;
  const email = contacts.find((x) => x.clientId === job.clientId && x.kind === "email" && x.isMain)?.value ?? null;
  return {
    ...card(f),
    lines: (row?.lines ?? jobLines(job, f.pv.originalDate)).map(lineRow),
    linesEdited: Boolean(row?.lines),
    client: { id: job.clientId, name: nameOf(client), phone, email },
    property: prop ? { id: prop.id, address: oneLine(prop), summary: propertySummary(prop) } : null,
    officeNotes: job.officeNotes,
    crewNotes: job.crewNotes,
    timerStartedAt: row?.timerStartedAt ?? null,
    timerMinutes: row?.timerMinutes ?? 0,
    completedAt: row?.completedAt ?? null,
    completedBy: row?.completedBy ?? null,
    crewCollected: row?.crewCollected ?? null,
    notesList: row ? notesOn("visits", row.id) : [],
    photos: row ? filesOn("visits", row.id) : [],
    checklist: list
      ? {
          id: list.id,
          name: list.name,
          items: list.items.map((i) => ({
            id: i.id,
            label: i.label,
            labelEs: i.labelEs,
            room: i.room ?? null,
            copy: 1,
            must: false,
            done: Boolean(row?.checks[i.id]),
            doneBy: row?.checks[i.id] ?? null,
          })),
          photosNeeded: 0,
          photosTaken: 0,
          left: [],
        }
      : null,
    repeating: job.frequency !== "one-time",
    continuedTo: null,
    continuedFrom: null,
    part: null,
    fixFor: null,
  };
}

function planned(companyId: string, from: string, to: string, only?: (j: Job) => boolean): Found[] {
  const rows = visits.filter((v) => v.companyId === companyId);
  const byId = new Map(rows.map((v) => [v.id, v]));
  return jobs
    .filter((j) => j.companyId === companyId && (!only || only(j)))
    .flatMap((job) => planVisits(job, rows, from, to).map((pv) => ({ job, pv, row: pv.id ? byId.get(pv.id) : undefined })));
}

function findVisit(companyId: string, ref: string): Found | null {
  const parsed = parseVisitRef(ref);
  if (!parsed) return null;
  if (parsed.kind === "row") {
    const row = visits.find((v) => v.id === parsed.id && v.companyId === companyId);
    const job = row && jobs.find((j) => j.id === row.jobId);
    if (!row || !job) return null;
    return {
      job,
      row,
      pv: {
        ref: row.id,
        id: row.id,
        jobId: job.id,
        date: row.date,
        originalDate: row.originalDate,
        moved: row.date !== row.originalDate,
        status: row.status,
      },
    };
  }
  const job = jobs.find((j) => j.id === parsed.jobId && j.companyId === companyId);
  if (!job || !isDue(job, parsed.date)) return null;
  const row = visits.find((v) => v.jobId === job.id && v.originalDate === parsed.date);
  if (row) return findVisit(companyId, row.id);
  return {
    job,
    row: undefined,
    pv: {
      ref: computedRef(job.id, parsed.date),
      id: null,
      jobId: job.id,
      date: parsed.date,
      originalDate: parsed.date,
      moved: false,
      status: "scheduled",
    },
  };
}

/** A computed visit becomes a row the first time it is changed. */
function touch(f: Found): Found & { row: Visit } {
  if (f.row) return { ...f, row: f.row };
  const row = touchRow(f.job, f.pv.originalDate);
  return { job: f.job, row, pv: { ...f.pv, ref: row.id, id: row.id } };
}

const fresh = (companyId: string, row: Visit) => {
  const f = findVisit(companyId, row.id);
  return f ? detail(f) : null;
};

function clientStats(c: Client) {
  const mine = jobs.filter((j) => j.clientId === c.id);
  const ids = new Set(mine.map((j) => j.id));
  const doneRows = visits.filter((v) => ids.has(v.jobId) && v.status === "done").sort((a, z) => z.date.localeCompare(a.date));
  const done = doneRows.map((v) => v.date);
  const next = planned(c.companyId, TODAY, addDays(TODAY, 62), (j) => ids.has(j.id))
    .filter((f) => !f.row || f.row.status === "scheduled")
    .map((f) => f.pv.date)
    .sort()[0];
  const newest = [...mine].sort((a, z) => Number(z.active) - Number(a.active) || z.startDate.localeCompare(a.startDate))[0];
  const named = (s: string | null | undefined) => (s?.trim() ? s.trim() : null);
  const service = named(newest?.service) ?? named(newest?.title);
  const money = clientMoneyHook.facts(c.id);
  const last = lastActivity({ ...money.times, visit_done: doneRows.find((v) => v.completedAt)?.completedAt ?? null });
  return {
    activeJobs: mine.filter((j) => j.active).length,
    lastVisit: done.sort().at(-1) ?? null,
    nextVisit: next ?? null,
    service,
    category: serviceCategory(service),
    onPlan: mine.some((j) => j.active && j.frequency !== "one-time"),
    lifetimeCents: money.lifetimeCents,
    lastActivity: last ? { what: last.what, at: last.at } : null,
    atRisk: riskWords({
      openComplaintOn: null,
      lowReview: null,
      pastDueDays: money.pastDueDays,
      lastCrews: doneRows.slice(0, 4).map((v) => v.crew ?? []),
    }),
  };
}

/** The server's saveContacts: extra phones and emails from the client form (main ones stay). */
function saveDemoContacts(companyId: string, clientId: string, list: ContactEdit[]) {
  const keep = new Set(list.flatMap((x) => (x.id ? [x.id] : [])));
  for (let i = contacts.length - 1; i >= 0; i--) {
    const x = contacts[i]!;
    if (x.clientId === clientId && !x.isMain && !keep.has(x.id)) contacts.splice(i, 1);
  }
  for (const x of list) {
    const old = x.id ? contacts.find((y) => y.id === x.id && y.clientId === clientId) : undefined;
    const v = { kind: x.kind, value: x.value, type: x.type ?? null, label: x.label ?? null };
    if (old) Object.assign(old, v);
    else contacts.push({ ...v, companyId, clientId, id: newId(), isMain: false, optedOut: null });
  }
}

function clientRow(c: Client): ClientRow {
  const p = mainProperty(c.id);
  return {
    id: c.id,
    name: clientName(c),
    isCompany: Boolean(c.companyName) || c.isCompany === true,
    companyName: c.companyName,
    phone: c.phone,
    email: c.email,
    address: p ? oneLine(p) : null,
    tags: c.tags,
    leadSource: c.leadSource,
    active: c.active,
    inactiveReason: c.inactiveReason,
    mergedIntoId: c.mergedIntoId,
    ...clientStats(c),
    createdAt: c.createdAt,
  };
}

function clientDetail(c: Client): ClientDetail {
  const business = Boolean(c.companyName) || c.isCompany === true;
  return {
    ...clientRow(c),
    firstName: c.firstName,
    lastName: c.lastName,
    prefers: c.prefers,
    noMarketing: c.noMarketing,
    notes: c.notes,
    officeNotes: c.officeNotes,
    inactiveNote: c.inactiveNote,
    inactiveAt: c.inactiveAt,
    contacts: contacts
      .filter((x) => x.clientId === c.id)
      .map(({ id, kind, value, type, label, isMain, optedOut }) => ({
        id,
        kind,
        value,
        type: type ?? null,
        label,
        isMain,
        optedOut,
      })),
    properties: properties
      .filter((p) => p.clientId === c.id)
      .map(({ companyId: _c, clientId: _k, ...p }) => ({ ...p, summary: propertySummary(p) })),
    notesList: notesOn("clients", c.id),
    files: filesOn("clients", c.id),
    kind: business ? "business" : "home",
    // Home clients pay by a saved card in the preview; businesses get invoices with no card.
    card: business ? null : { brand: "Visa", last4: "4242", expMonth: 8, expYear: 2028 },
    autoCharge: !business,
    paymentTerms: paymentTermsWords(business, { pastDueHoursHome: 24, pastDueDaysBusiness: 7 }),
    referredBy:
      c.referredById && mineClientAny(c.referredById)
        ? { id: c.referredById, name: nameOf(mineClientAny(c.referredById)) }
        : null,
    billingAddress: c.billingAddress ?? null,
    billing: c.billing
      ? { ...c.billing, street2: c.billing.street2 ?? null }
      : c.billingAddress
        ? splitAddress(c.billingAddress)
        : null,
  };
}

/** Recent pricing in the preview: each service's price on the client's jobs, quoted the same when the job began. */
function clientPricing(clientId: string): PricingRow[] {
  const seen = new Map<string, PricingRow>();
  for (const j of jobs.filter((x) => x.clientId === clientId).sort((a, b) => b.startDate.localeCompare(a.startDate)))
    for (const l of j.lines) {
      const k = l.description.trim().toLowerCase();
      if (seen.has(k)) continue;
      seen.set(k, {
        description: l.description,
        quotedCents: l.amountCents,
        quotedOn: addDays(j.startDate, -7),
        quoteId: null,
        jobCents: l.amountCents,
        jobOn: j.startDate,
        jobId: j.id,
        jobNumber: j.number,
      });
    }
  return [...seen.values()].slice(0, 10);
}

function jobRow(j: Job): JobRow {
  const prop = propertyOf(j);
  const next = planned(j.companyId, TODAY, addDays(TODAY, 62), (x) => x.id === j.id)
    .filter((f) => !f.row || f.row.status === "scheduled")
    .map((f) => f.pv.date)
    .sort()[0];
  return {
    id: j.id,
    number: j.number,
    clientId: j.clientId,
    clientName: nameOf(clientOf(j.clientId)),
    name: jobName(j),
    frequency: j.frequency,
    frequencyLabel: repeatWords({ frequency: j.frequency, startDate: j.startDate, repeatDay: j.repeatDay ?? null }),
    repeatRule: null,
    repeatFrom: null,
    startDate: j.startDate,
    repeatDay: j.repeatDay ?? null,
    startTime: j.startTime,
    endsOn: j.endsOn,
    active: j.active,
    address: prop ? oneLine(prop) : null,
    crew: chips(j.crew),
    visitTotalCents: jobLines(j, next ?? TODAY)
      .filter((l) => l.frequency === "every")
      .reduce((s, l) => s + l.amountCents, 0),
    nextVisit: next ?? null,
    completedOn:
      j.frequency === "one-time" && !next
        ? (visits
            .filter((v) => v.jobId === j.id && v.status === "done")
            .map((v) => v.date)
            .sort()
            .at(-1) ?? null)
        : null,
  };
}

function jobDetail(j: Job): JobDetail {
  const list = checklists.find((c) => c.id === j.checklistId);
  return {
    ...jobRow(j),
    waitingOk: null,
    title: j.title,
    service: j.service,
    propertyId: j.propertyId,
    arriveFrom: j.arriveFrom,
    arriveTo: j.arriveTo,
    hours: j.hours,
    billing: j.billing,
    officeNotes: j.officeNotes,
    crewNotes: j.crewNotes,
    crewNotesEs: j.crewNotesEs,
    checklist: list ? { id: list.id, name: list.name } : null,
    lastDone:
      visits
        .filter((v) => v.jobId === j.id && v.status === "done" && v.date <= TODAY)
        .map((v) => v.date)
        .sort()
        .at(-1) ?? null,
    skipDates: j.skipDates,
    // The lines in use from today on (ended ones stay on the visits they billed).
    lines: j.lines.filter((l) => !l.endsOn || l.endsOn >= TODAY).map(lineRow),
    notesList: notesOn("jobs", j.id),
    autoPay: j.autoPay ?? true,
    autoCharge: j.frequency === "one-time" ? "one_time" : (j.autoPay ?? true) ? "client_off" : "job_off",
  };
}

/** A job's expenses, and labor from its done visits at a flat 40% of each visit's price split by the crew (demo only). */
function jobCostsOf(j: Job, seesPay: boolean): JobCosts {
  const expenses = jobExpenses
    .filter((e) => e.jobId === j.id && !e.gone)
    .sort((a, b) => b.spentOn.localeCompare(a.spentOn))
    .map(({ companyId: _c, jobId: _j, gone: _g, ...e }) => e);
  const expenseCents = expenses.reduce((a, e) => a + e.amountCents, 0);
  const done = visits.filter((v) => v.jobId === j.id && v.status === "done").sort((a, b) => b.date.localeCompare(a.date));
  const baseOf = (v: Visit) => (v.lines ?? jobLines(j, v.originalDate)).reduce((a, l) => a + l.amountCents, 0);
  const timed = done.filter((v) => v.timerMinutes > 0);
  const minutes = timed.reduce((a, v) => a + v.timerMinutes, 0);
  const billed = timed.reduce((a, v) => a + baseOf(v), 0);
  const starter = personOf(j.crew[0] ?? "");
  const time: JobTime = {
    avgMinutes: timed.length ? Math.round(minutes / timed.length) : null,
    timedVisits: timed.length,
    perHourCents: minutes > 0 && billed > 0 ? Math.round((billed * 60) / minutes) : null,
    log: visits
      .filter((v) => v.jobId === j.id && (v.timerMinutes > 0 || v.timerStartedAt))
      .sort((a, b) => b.date.localeCompare(a.date))
      .map((v) => {
        const stop = v.timerStartedAt ? null : (v.completedAt ?? null);
        const start = v.timerStartedAt ?? (stop ? new Date(Date.parse(stop) - v.timerMinutes * 60_000).toISOString() : null);
        return {
          visitId: v.id,
          date: v.date,
          done: v.status === "done",
          minutes: v.timerMinutes,
          startedAt: start,
          stoppedAt: stop,
          running: v.timerStartedAt !== null,
          startedBy: starter ? shortName(starter.firstName, starter.lastName) : null,
        };
      }),
  };
  if (!seesPay) return { expenses, expenseCents, time, labor: null, profit: null };
  const labor: JobLaborRow[] = [];
  let revenue = 0;
  for (const v of done) {
    const base = baseOf(v);
    revenue += base;
    const crew = chips(v.crew ?? j.crew);
    for (const person of crew)
      labor.push({
        visitId: v.id,
        date: v.date,
        person,
        minutes: v.timerMinutes || Math.round((j.hours ?? 2) * 60),
        payCents: Math.round((base * 0.4) / crew.length),
      });
  }
  const laborCents = labor.reduce((a, l) => a + l.payCents, 0);
  return {
    expenses,
    expenseCents,
    time,
    labor,
    profit: {
      visits: done.length,
      laborMinutes: labor.reduce((a, l) => a + l.minutes, 0),
      ...jobProfit(revenue, laborCents, expenseCents),
    },
  };
}

const toLines = (input: z.infer<typeof visitLinesBody>["lines"], startDate: string | null): Line[] =>
  input.map((l) => ({
    id: newId(),
    description: l.description,
    note: l.note ?? null,
    quantity: l.quantity,
    unitPriceCents: l.unitPriceCents,
    amountCents: amount(l.quantity, l.unitPriceCents),
    frequency: l.frequency,
    startDate,
    endsOn: null,
    answers: l.answers ?? null,
  }));

/** "This and future visits": the old lines end the day before, the new ones start that day. */
function replaceLinesFrom(j: Job, from: string, input: z.infer<typeof visitLinesBody>["lines"]) {
  if (j.frequency === "one-time" || from <= j.startDate) {
    j.lines = toLines(input, null);
    return;
  }
  j.lines = j.lines
    .filter((l) => (l.startDate ?? j.startDate) < from)
    .map((l) => (!l.endsOn || l.endsOn >= from ? { ...l, endsOn: addDays(from, -1) } : l));
  j.lines.push(...toLines(input, from));
}

/** Visits of the company for Home, the report and the crew's day. */
export function homeAnswer(companyId: string): HomeAnswer {
  const live = (from: string, to: string) =>
    planned(companyId, from, to).filter((f) => !["cancelled", "skipped"].includes(f.row?.status ?? "scheduled"));
  const monday = addDays(TODAY, -((new Date(`${TODAY}T12:00:00Z`).getUTCDay() + 6) % 7));
  const value = (fs: Found[]) => fs.reduce((s, f) => s + card(f).totalCents, 0);
  const today = live(TODAY, TODAY)
    .map(card)
    .sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? ""));
  return {
    today: TODAY,
    todayVisits: today.map((v) => ({
      jobId: v.jobId,
      date: v.date,
      startTime: v.startTime,
      clientName: v.clientName,
      address: v.address,
      jobName: v.jobName,
      crew: v.crew.map((p) => p.name),
      valueCents: v.totalCents,
    })),
    todayValueCents: today.reduce((s, v) => s + v.totalCents, 0),
    weekValueCents: value(live(monday, addDays(monday, 6))),
    lastWeekValueCents: value(live(addDays(monday, -7), addDays(monday, -1))),
    weekVisits: live(monday, addDays(monday, 6)).length,
    lastWeekVisits: live(addDays(monday, -7), addDays(monday, -1)).length,
    monthValueCents: value(live(addDays(TODAY, -29), TODAY)),
    lastMonthValueCents: value(live(addDays(TODAY, -59), addDays(TODAY, -30))),
    monthVisits: live(addDays(TODAY, -29), TODAY).length,
    lastMonthVisits: live(addDays(TODAY, -59), addDays(TODAY, -30)).length,
    nextSevenDaysVisits: live(TODAY, addDays(TODAY, 6)).length,
    activeClients: clients.filter((c) => c.companyId === companyId && c.active && !c.mergedIntoId).length,
    activeJobs: jobs.filter((j) => j.companyId === companyId && j.active).length,
  };
}

export function workCounts(companyId: string): { clients: number; activeJobs: number } {
  return {
    clients: clients.filter((c) => c.companyId === companyId && c.active && !c.mergedIntoId).length,
    activeJobs: jobs.filter((j) => j.companyId === companyId && j.active).length,
  };
}

// Money bridge (draft 3) ---------------------------------------------------------------------------------------------

/** What the preview's money data (money.ts) reads from clients and jobs. */
export interface WorkClient {
  id: string;
  companyId: string;
  name: string;
  firstName: string;
  phone: string | null;
  email: string | null;
  companyName: string | null;
  address: string | null;
}
export interface DoneVisit {
  companyId: string;
  visitId: string;
  clientId: string;
  jobId: string;
  jobName: string;
  frequency: string;
  billing: string;
  date: string;
  lines: { description: string; quantity: number; unitPriceCents: number; amountCents: number }[];
  crewCollected: "cash" | "check" | "company" | null;
  /** Person ids: the visit's own crew, else its job's. */
  crew: string[];
  clientName: string;
}
const asWorkClient = (c: Client): WorkClient => {
  const pr = mainProperty(c.id);
  return {
    id: c.id,
    companyId: c.companyId,
    name: clientName(c),
    firstName: (c.firstName ?? clientName(c).split(" ")[0] ?? "").trim(),
    phone: c.phone,
    email: c.email,
    companyName: c.companyName,
    address: pr ? oneLine(pr) : null,
  };
};
function doneOf(v: Visit): DoneVisit | null {
  const job = jobs.find((j) => j.id === v.jobId);
  if (!job) return null;
  return {
    companyId: v.companyId,
    visitId: v.id,
    clientId: job.clientId,
    jobId: job.id,
    jobName: jobName(job),
    frequency: job.frequency,
    billing: job.billing,
    date: v.date,
    lines: (v.lines ?? jobLines(job, v.originalDate)).map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
      amountCents: l.amountCents,
    })),
    crewCollected: v.crewCollected,
    crew: v.crew ?? job.crew,
    clientName: (() => {
      const c = clientOf(job.clientId);
      return c ? clientName(c) : "";
    })(),
  };
}
export const workLookup = {
  client: (id: string): WorkClient | null => {
    const c = clientOf(id);
    return c ? asWorkClient(c) : null;
  },
  clients: (companyId: string): WorkClient[] =>
    clients.filter((c) => c.companyId === companyId && c.active && !c.mergedIntoId).map(asWorkClient),
  /** Other clients with this phone or email, for the Send box warning. */
  sharing: (companyId: string, clientId: string, to: string): string[] =>
    clients
      .filter((c) => c.companyId === companyId && c.id !== clientId && !c.mergedIntoId && (c.phone === to || c.email === to))
      .map((c) => clientName(c)),
  /** Done visits, for the preview's first invoices. */
  doneVisits: (companyId: string): DoneVisit[] =>
    visits
      .filter((v) => v.companyId === companyId && v.status === "done")
      .flatMap((v) => {
        const d = doneOf(v);
        return d ? [d] : [];
      }),
  /** A new client from an approved quote. */
  addClient: (companyId: string, name: string, phone: string | null, email: string | null): WorkClient => {
    const [firstName = name, ...rest] = name.trim().split(/\s+/);
    const c = addClient(companyId, {
      first: firstName,
      last: rest.join(" "),
      phone: phone ?? "",
      created: 0,
      ...(email ? { email } : {}),
    });
    return asWorkClient(c);
  },
  /** The job made from an approved quote. */
  addJob: (
    clientId: string,
    title: string,
    frequency: string,
    startDate: string,
    startTime: string | null,
    lines: { description: string; amountCents: number }[],
  ): string | null => {
    const c = clientOf(clientId);
    if (!c) return null;
    const j = addJob(c, {
      title,
      service: title,
      frequency,
      start: daysBetween(TODAY, startDate),
      time: startTime,
      hours: 2,
      crew: [],
      lines: lines.map((l) => [l.description, l.amountCents]),
    });
    return j.id;
  },
};
/** money.ts listens here: a visit marked done goes on an invoice, a reopened one comes off. */
export const visitHooks: { done: (v: DoneVisit) => void; reopened: (visitId: string) => void } = {
  done: () => undefined,
  reopened: () => undefined,
};

/** Money facts for the client list's columns and tiles, filled in by money.ts (which imports this file, not the other way). */
export const clientMoneyHook: {
  facts: (clientId: string) => {
    lifetimeCents: number;
    times: Partial<Record<ActivityKind, string | null>>;
    pastDueDays: number;
  };
  leadsBetween: (companyId: string, from: string, to: string) => number;
} = {
  facts: () => ({ lifetimeCents: 0, times: {}, pastDueDays: 0 }),
  leadsBetween: () => 0,
};

// Routes -------------------------------------------------------------------------------------------------------------

/** Answers the draft 2 routes, or null when the route is not one of them. */
export function workRoute(ctx: WorkCtx, method: string, p: string, query: URLSearchParams, b: unknown): Answer | null {
  const cid = ctx.companyId;
  const seg = p.split("/");
  const route = `${method} ${p}`;
  const { gate } = ctx;
  const mineClient = (id: string | undefined) => clients.find((c) => c.id === id && c.companyId === cid && !c.mergedIntoId);
  const mineJob = (id: string | undefined) => jobs.find((j) => j.id === id && j.companyId === cid);

  // Search and the crew's day
  if (route === "GET search") {
    if (!gate("client.read")) return NOT_ALLOWED();
    const q = (query.get("q") ?? "").trim();
    if (!q) return fail(400, "bad_request", "Type something to search for.");
    const found = clients
      .filter((c) => c.companyId === cid && !c.mergedIntoId)
      .filter((c) =>
        searchMatches(
          [clientName(c), c.firstName, c.lastName, c.phone, c.email, mainProperty(c.id)?.address].filter(Boolean).join(" "),
          q,
        ),
      )
      .sort((a, z) => Number(z.active) - Number(a.active))
      .slice(0, 6);
    const jobsFound = jobs
      .filter((j) => j.companyId === cid)
      .filter((j) => searchMatches(`${jobName(j)} ${nameOf(clientOf(j.clientId))} #${String(j.number)}`, q))
      .slice(0, 5);
    const ids = new Set(found.map((c) => c.id));
    const visitsFound = planned(cid, TODAY, addDays(TODAY, 14), (j) => ids.has(j.clientId))
      .map(card)
      .sort((a, z) => a.date.localeCompare(z.date))
      .slice(0, 5);
    return ok({
      clients: found.map((c) => ({
        id: c.id,
        name: clientName(c),
        phone: c.phone,
        address: (() => {
          const pr = mainProperty(c.id);
          return pr ? oneLine(pr) : null;
        })(),
        active: c.active,
      })),
      jobs: jobsFound.map((j) => ({
        id: j.id,
        number: j.number,
        name: jobName(j),
        clientName: nameOf(clientOf(j.clientId)),
      })),
      visits: visitsFound,
    });
  }
  if (route === "GET my-day") {
    if (!gate("myday.read")) return NOT_ALLOWED();
    const date = query.get("date") ?? TODAY;
    const me = ctx.people.find((x) => x.userId === ctx.me.id && x.companyId === cid);
    if (!me) return ok([]);
    const prices = ctx.role !== "crew" || me.seesPrices;
    const out: MyVisit[] = planned(cid, date, date)
      .filter((f) => (f.row?.crew ?? f.job.crew).includes(me.id) && f.row?.status !== "cancelled")
      .map((f) => {
        const v = card(f);
        const es = me.locale === "es" && f.job.crewNotesEs;
        return {
          ref: v.ref,
          date: v.date,
          startTime: v.startTime,
          endTime: v.endTime,
          status: v.status,
          jobName: v.jobName,
          clientName: v.clientName,
          address: v.address,
          crewNotes: es ? f.job.crewNotesEs : f.job.crewNotes,
          crew: v.crew.map((x) => x.name),
          ...(prices ? { priceCents: v.totalCents } : {}),
        };
      })
      .sort((a, z) => (a.startTime ?? "").localeCompare(z.startTime ?? ""));
    return ok(out);
  }

  // One of my visits (the crew visit page) and its messages with the client. Made up, nothing saves.
  if (method === "GET" && seg[0] === "my-day" && seg.length === 3 && seg[2] === "messages") {
    if (!gate("myday.read")) return NOT_ALLOWED();
    return ok({ canWrite: true, messages: [] });
  }
  if (method === "GET" && seg[0] === "my-day" && seg.length === 2) {
    if (!gate("myday.read")) return NOT_ALLOWED();
    const ref = decodeURIComponent(seg[1]!);
    const date = ref.split(".")[1] ?? TODAY;
    const f = planned(cid, date, date).find((x) => card(x).ref === ref);
    if (!f) return NOT_FOUND("visit");
    const v = card(f);
    const detail: MyVisitDetail = {
      ref: v.ref,
      date: v.date,
      startTime: v.startTime,
      endTime: v.endTime,
      status: v.status,
      jobName: v.jobName,
      clientName: v.clientName,
      clientPhone: "+15045550140",
      address: v.address,
      crewNotes: f.job.crewNotes,
      crew: v.crew.map((x) => x.name),
      lat: null,
      lng: null,
      work: (f.row?.lines ?? jobLines(f.job, v.originalDate)).map((l) => l.description),
      home: "3 bed, 2 bath, 1 more room",
      checklist: null,
      timerStartedAt: null,
      timerMinutes: 0,
      notes: [],
      photos: [],
      completedAt: null,
      crewCollected: null,
      asksCashOrCheck: false,
    };
    return ok(detail);
  }

  // Clients
  if (route === "GET clients") {
    if (!gate("client.read")) return NOT_ALLOWED();
    const status = query.get("status") ?? "active";
    const tag = query.get("tag");
    const q = query.get("q")?.trim();
    const sort = query.get("sort") ?? "name";
    const show = query.get("show");
    const usual = ["newest", "last_activity", "lifetime"].includes(sort) ? "desc" : "asc";
    const flip = (query.get("dir") ?? usual) === usual ? 1 : -1;
    const noJob = (id: string) => !jobs.some((j) => j.clientId === id && j.active);
    const rows = clients
      .filter((c) => c.companyId === cid && !c.mergedIntoId)
      .filter((c) => status === "all" || (status === "active" ? c.active : !c.active))
      .filter((c) => !tag || c.tags.includes(tag))
      .filter((c) => show !== "nothing_booked" || (c.active && noJob(c.id)))
      .filter(
        (c) =>
          !q ||
          searchMatches(
            [clientName(c), c.firstName, c.lastName, c.phone, c.email, mainProperty(c.id)?.address].filter(Boolean).join(" "),
            q,
          ),
      )
      .map(clientRow)
      .filter((r) => (show === "plan" ? r.onPlan : show === "at_risk" ? r.active && r.atRisk : true));
    const by = (r: ClientRow): string | number | null =>
      sort === "newest"
        ? r.createdAt
        : sort === "next_visit"
          ? r.nextVisit
          : sort === "last_activity"
            ? (r.lastActivity?.at ?? null)
            : sort === "lifetime"
              ? r.lifetimeCents
              : r.name.toLowerCase();
    rows.sort((a, z) => {
      const x = by(a);
      const y = by(z);
      if (x === y) return a.name.localeCompare(z.name);
      if (x === null) return 1;
      if (y === null) return -1;
      return (x < y ? -1 : 1) * (usual === "asc" ? 1 : -1) * flip;
    });
    return ok({ items: rows, next: null });
  }
  if (route === "GET clients/summary") {
    if (!gate("client.read")) return NOT_ALLOWED();
    const mine = clients.filter((c) => c.companyId === cid && !c.mergedIntoId);
    const monthStart = `${TODAY.slice(0, 8)}01`;
    const day30 = addDays(TODAY, -29);
    const day60 = addDays(TODAY, -59);
    const made = (c: Client) => c.createdAt.slice(0, 10);
    const tags = new Map<string, number>();
    for (const c of mine.filter((x) => x.active)) for (const t of c.tags) tags.set(t, (tags.get(t) ?? 0) + 1);
    const activeRows = mine.filter((c) => c.active).map(clientRow);
    return ok({
      active: activeRows.length,
      inactive: mine.filter((c) => !c.active).length,
      newThisMonth: mine.filter((c) => made(c) >= monthStart).length,
      withoutJobs: mine.filter((c) => c.active && !jobs.some((j) => j.clientId === c.id && j.active)).length,
      leads30: clientMoneyHook.leadsBetween(cid, day30, TODAY),
      leadsBefore30: clientMoneyHook.leadsBetween(cid, day60, addDays(day30, -1)),
      new30: mine.filter((c) => made(c) >= day30).length,
      newBefore30: mine.filter((c) => made(c) >= day60 && made(c) < day30).length,
      newThisYear: mine.filter((c) => made(c) >= `${TODAY.slice(0, 4)}-01-01`).length,
      onPlan: activeRows.filter((r) => r.onPlan).length,
      atRisk: activeRows.filter((r) => r.atRisk).length,
      tags: [...tags].map(([t, count]) => ({ tag: t, count })).sort((a, z) => z.count - a.count),
    });
  }
  if (route === "POST clients/bulk") {
    if (!gate("client.bulk")) return NOT_ALLOWED();
    const parsed = bulkClientsBody.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const body = parsed.data;
    const targets = body.ids.map(mineClient).filter((c): c is Client => Boolean(c));
    if (targets.length !== body.ids.length) return NOT_FOUND("client");
    if (body.ids.length > BULK_LIMIT) {
      if (ctx.role !== "owner" && ctx.role !== "franchise_owner")
        return fail(403, "bulk_needs_owner", `Changing more than ${String(BULK_LIMIT)} clients at once needs the owner.`);
      const token = `confirm-${body.action}-${String(body.ids.length)}`;
      if (body.confirm !== token)
        return fail(
          409,
          "confirm_needed",
          `This changes ${String(body.ids.length)} clients at once. Check the list, then confirm to go ahead.`,
          { confirm: token },
        );
    }
    if ((body.action === "tag" || body.action === "untag") && !body.tag) return fail(400, "bad_request", "Type the tag.");
    for (const c of targets) {
      if (body.action === "deactivate") {
        c.active = false;
        c.inactiveReason = body.reason ?? "other";
        c.inactiveAt = new Date().toISOString();
      } else if (body.action === "reactivate") {
        c.active = true;
        c.inactiveReason = null;
        c.inactiveAt = null;
        c.inactiveNote = null;
      } else if (body.action === "tag" && body.tag && !c.tags.includes(body.tag)) c.tags.push(body.tag);
      else if (body.action === "untag") c.tags = c.tags.filter((t) => t !== body.tag);
    }
    ctx.log("client.bulk");
    return ok({ done: targets.length, ids: targets.map((c) => c.id) });
  }
  if (route === "POST clients") {
    if (!gate("client.update")) return NOT_ALLOWED();
    const parsed = clientInput.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const d = parsed.data;
    const c = addClient(cid, {
      first: d.firstName,
      ...(d.lastName ? { last: d.lastName } : {}),
      ...(d.companyName ? { company: d.companyName } : {}),
      phone: d.phone,
      ...(d.email ? { email: d.email } : {}),
      tags: d.tags ?? [],
      ...(d.leadSource ? { source: d.leadSource } : {}),
      created: 0,
    });
    c.prefers = d.prefers ?? "text";
    c.noMarketing = d.noMarketing ?? false;
    c.notes = d.notes ?? null;
    c.officeNotes = d.officeNotes ?? null;
    c.isCompany = d.isCompany === true;
    c.billing = d.billing ?? null;
    c.billingAddress = d.billing ? partsLine(d.billing) : (d.billingAddress ?? null);
    properties.push(newProperty(cid, c.id, d.property));
    saveDemoContacts(cid, c.id, d.contacts ?? []);
    ctx.log("client.create");
    return ok(clientDetail(c), 201);
  }
  if (seg[0] === "clients" && seg[1]) {
    const c = mineClient(seg[1]);
    if (!c) return NOT_FOUND("client");
    const sub = seg[2];
    if (method === "GET" && !sub) return gate("client.read") ? ok(clientDetail(c)) : NOT_ALLOWED();
    if (method === "GET" && sub === "pricing") return gate("client.read") ? ok(clientPricing(c.id)) : NOT_ALLOWED();
    if (method === "GET" && sub === "dispute-file")
      return gate("dispute.read") ? ok(demoDisputeFile(c.id, clientName(c))) : NOT_ALLOWED();
    if (method === "GET" && sub === "reminders")
      return gate("client.read")
        ? ok(reminders.filter((r) => r.clientId === c.id).map(({ clientId: _c, companyId: _k, ...r }) => r))
        : NOT_ALLOWED();
    if (sub === "merge" && method === "POST") {
      if (!gate("client.merge")) return NOT_ALLOWED();
      const parsed = mergeBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const keep = mineClient(parsed.data.into);
      if (!keep || keep.id === c.id) return fail(400, "bad_request", "Pick the client to keep. It must be a different one.");
      if (jobs.some((j) => j.clientId === c.id && j.active && j.frequency !== "one-time"))
        return fail(
          409,
          "has_repeating_schedule",
          `${clientName(c)} still has a repeating job. End it or move it to ${clientName(keep)} first, then merge.`,
        );
      const moved = { jobs: 0, visits: 0, properties: 0, contacts: 0, notes: 0, files: 0 };
      for (const j of jobs.filter((x) => x.clientId === c.id)) {
        j.clientId = keep.id;
        moved.jobs++;
        moved.visits += visits.filter((v) => v.jobId === j.id).length;
      }
      for (const pr of properties.filter((x) => x.clientId === c.id)) {
        if (properties.some((x) => x.clientId === keep.id && x.address === pr.address)) continue;
        pr.clientId = keep.id;
        moved.properties++;
      }
      for (const ct of contacts.filter((x) => x.clientId === c.id)) {
        const same = (v: string) => v.replace(/\D/g, "") || v.toLowerCase();
        if (contacts.some((x) => x.clientId === keep.id && same(x.value) === same(ct.value))) continue;
        ct.clientId = keep.id;
        ct.isMain = false;
        moved.contacts++;
      }
      for (const n of notes.filter((x) => x.on === "clients" && x.subjectId === c.id)) {
        n.subjectId = keep.id;
        moved.notes++;
      }
      for (const f of files.filter((x) => x.on === "clients" && x.subjectId === c.id)) {
        f.subjectId = keep.id;
        moved.files++;
      }
      c.active = false;
      c.mergedIntoId = keep.id;
      c.inactiveAt = new Date().toISOString();
      ctx.log("client.merge");
      return ok({ kept: keep.id, closed: c.id, moved });
    }
    if (!gate("client.update")) return NOT_ALLOWED();
    if (method === "PATCH" && !sub) {
      const parsed = clientPatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const d = parsed.data;
      for (const key of [
        "firstName",
        "lastName",
        "companyName",
        "phone",
        "email",
        "leadSource",
        "notes",
        "officeNotes",
      ] as const) {
        const v = d[key];
        if (v !== undefined) c[key] = v;
      }
      if (d.tags) c.tags = d.tags;
      if (d.billing !== undefined) {
        c.billing = d.billing;
        c.billingAddress = d.billing ? partsLine(d.billing) : null;
      } else if (d.billingAddress !== undefined) {
        c.billing = null;
        c.billingAddress = d.billingAddress;
      }
      if (d.referredById !== undefined) c.referredById = d.referredById;
      if (d.prefers) c.prefers = d.prefers;
      if (d.noMarketing !== undefined) c.noMarketing = d.noMarketing;
      if (d.isCompany !== undefined) c.isCompany = d.isCompany;
      if (d.contacts) saveDemoContacts(cid, c.id, d.contacts);
      if (!clientName(c) || clientName(c) === "Client")
        return fail(400, "bad_request", "Give a first or last name, or a company name.");
      for (const kind of ["phone", "email"] as const) {
        const main = contacts.find((x) => x.clientId === c.id && x.kind === kind && x.isMain);
        const value = c[kind];
        if (main && value) main.value = value;
        else if (!main && value)
          contacts.push({ companyId: cid, clientId: c.id, id: newId(), kind, value, label: null, isMain: true, optedOut: null });
      }
      ctx.log("client.update");
      return ok(clientDetail(c));
    }
    if (method === "POST" && sub === "deactivate") {
      const parsed = deactivateBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      c.active = false;
      c.inactiveReason = parsed.data.reason;
      c.inactiveNote = parsed.data.note ?? null;
      c.inactiveAt = new Date().toISOString();
      ctx.log("client.deactivate");
      return ok(clientDetail(c));
    }
    if (method === "POST" && sub === "reactivate") {
      c.active = true;
      c.inactiveReason = null;
      c.inactiveNote = null;
      c.inactiveAt = null;
      ctx.log("client.reactivate");
      return ok(clientDetail(c));
    }
    if (sub === "contacts") {
      if (method === "POST") {
        const parsed = contactInput.safeParse(b);
        if (!parsed.success) return bad(parsed.error);
        const d = parsed.data;
        if (d.isMain) for (const x of contacts.filter((y) => y.clientId === c.id && y.kind === d.kind)) x.isMain = false;
        contacts.push({
          companyId: cid,
          clientId: c.id,
          id: newId(),
          kind: d.kind,
          value: d.value,
          type: d.type ?? null,
          label: d.label ?? null,
          isMain: d.isMain ?? !contacts.some((y) => y.clientId === c.id && y.kind === d.kind),
          optedOut: null,
        });
        return ok(clientDetail(c), 201);
      }
    }
    if (sub === "properties") {
      if (method === "POST") {
        const parsed = propertyInput.safeParse(b);
        if (!parsed.success) return bad(parsed.error);
        properties.push(newProperty(cid, c.id, parsed.data));
        return ok(clientDetail(c), 201);
      }
    }
    if (sub === "thread" && method === "POST") {
      const channel = (b as { channel?: string } | null)?.channel === "email" ? "email" : "sms";
      const contact = channel === "email" ? c.email : c.phone;
      if (!contact)
        return fail(409, "no_contact", channel === "email" ? "They have no email yet." : "They have no phone number yet.");
      return ok({ id: demoThreadFor(c.id, clientName(c), channel, contact) });
    }
    if (sub === "reminders" && method === "POST") {
      const parsed = reminderInput.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const id = newId();
      reminders.push({
        companyId: cid,
        clientId: c.id,
        key: `t${id}`,
        kind: "task",
        id,
        dueOn: parsed.data.dueOn,
        dueTime: parsed.data.dueTime ?? null,
        title: parsed.data.title,
        sub: parsed.data.note ?? null,
        assignedName: ctx.me.name,
        done: false,
        quoteId: null,
        invoiceId: null,
      });
      return ok({ id }, 201);
    }
    if (sub === "reminders" && seg[3] && method === "PATCH") {
      const parsed = reminderPatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const r = reminders.find((x) => x.id === seg[3] && x.clientId === c.id);
      if (!r) return NOT_FOUND("reminder");
      r.done = parsed.data.done;
      return ok({ ok: true });
    }
    if (sub === "notes" && method === "POST") return addNote(ctx, "clients", c.id, b);
    if (sub === "files" && method === "POST") return addFile(ctx, "clients", c.id, b);
  }

  // Contacts and properties, by their own id
  if (seg[0] === "contacts" && seg[1] && method === "DELETE") {
    if (!gate("client.update")) return NOT_ALLOWED();
    const i = contacts.findIndex((x) => x.id === seg[1] && x.companyId === cid);
    if (i < 0) return NOT_FOUND("contact");
    contacts.splice(i, 1);
    return ok({ ok: true });
  }
  if (seg[0] === "properties" && seg[1] && method === "PATCH") {
    if (!gate("client.update")) return NOT_ALLOWED();
    const pr = properties.find((x) => x.id === seg[1] && x.companyId === cid);
    if (!pr) return NOT_FOUND("property");
    const parsed = propertyPatch.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    Object.assign(pr, Object.fromEntries(Object.entries(parsed.data).filter(([, v]) => v !== undefined)));
    return ok(pr);
  }

  // Notes and files
  if (seg[0] === "notes" && seg[1]) {
    const n = notes.find((x) => x.id === seg[1] && x.companyId === cid);
    if (!n) return NOT_FOUND("note");
    if (!gate(n.on === "clients" ? "client.update" : n.on === "jobs" ? "job.update" : "visit.update")) return NOT_ALLOWED();
    if (method === "PATCH") {
      const parsed = notePatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      Object.assign(n, Object.fromEntries(Object.entries(parsed.data).filter(([, v]) => v !== undefined)));
      return ok(noteRow(n));
    }
    if (method === "DELETE") {
      n.deleted = true;
      return ok({ ok: true });
    }
    if (method === "POST" && seg[2] === "restore") {
      n.deleted = false;
      return ok(noteRow(n));
    }
  }
  if (seg[0] === "files" && seg[1] && method === "DELETE") {
    const f = files.find((x) => x.id === seg[1] && x.companyId === cid);
    if (!f) return NOT_FOUND("file");
    if (!gate(f.on === "clients" ? "client.update" : "visit.update")) return NOT_ALLOWED();
    f.deleted = true;
    return ok({ ok: true });
  }

  // Jobs
  if (route === "GET jobs") {
    if (!gate("job.read")) return NOT_ALLOWED();
    const status = query.get("status") ?? "active";
    const kind = query.get("kind") ?? "all";
    const clientId = query.get("clientId");
    const q = query.get("q")?.trim();
    const rows = jobs
      .filter((j) => j.companyId === cid)
      .filter((j) => !clientId || j.clientId === clientId)
      .filter((j) => status === "all" || (status === "active" ? j.active : !j.active))
      .filter((j) => kind === "all" || (kind === "one-time" ? j.frequency === "one-time" : j.frequency !== "one-time"))
      .filter((j) => !q || searchMatches(`${jobName(j)} ${nameOf(clientOf(j.clientId))} ${String(j.number)}`, q))
      .map(jobRow)
      .sort((a, z) => (z.number ?? 0) - (a.number ?? 0));
    // A page at a time, as the server answers (limit and cursor).
    const limit = Number(query.get("limit")) || 50;
    const from = Number(query.get("cursor")) || 0;
    return ok({ items: rows.slice(from, from + limit), next: rows.length > from + limit ? String(from + limit) : null });
  }
  if (route === "POST jobs") {
    if (!gate("job.update")) return NOT_ALLOWED();
    const parsed = jobInput.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const d = parsed.data;
    const client = mineClient(d.clientId);
    if (!client) return NOT_FOUND("client");
    if (!client.active) return fail(409, "client_inactive", "That client is not active. Make them active first.");
    const j: Job = {
      companyId: cid,
      id: newId(),
      number: nextNumber(cid),
      clientId: client.id,
      propertyId: d.propertyId ?? mainProperty(client.id)?.id ?? null,
      title: d.title ?? null,
      service: d.service ?? null,
      frequency: d.frequency,
      startDate: d.startDate,
      repeatDay: d.repeatDay ?? null,
      startTime: d.startTime ?? null,
      arriveFrom: d.arriveFrom ?? null,
      arriveTo: d.arriveTo ?? null,
      hours: d.hours ?? null,
      endsOn: d.endsOn ?? null,
      crew: d.crew,
      billing: d.billing,
      officeNotes: d.officeNotes ?? null,
      crewNotes: d.crewNotes ?? null,
      crewNotesEs: d.crewNotesEs ?? null,
      checklistId: d.checklistId ?? null,
      skipDates: [],
      routeId: null,
      routePosition: null,
      active: true,
      lines: toLines(d.lines, null),
    };
    jobs.push(j);
    ctx.log("job.create");
    return ok(jobDetail(j), 201);
  }
  if (seg[0] === "jobs" && seg[1]) {
    const j = mineJob(seg[1]);
    if (!j) return NOT_FOUND("job");
    if (method === "GET" && !seg[2]) return gate("job.read") ? ok(jobDetail(j)) : NOT_ALLOWED();
    if (method === "GET" && seg[2] === "costs") return gate("job.read") ? ok(jobCostsOf(j, gate("payroll.read"))) : NOT_ALLOWED();
    if (!gate("job.update")) return NOT_ALLOWED();
    if (method === "POST" && seg[2] === "expenses") {
      const parsed = jobExpenseInput.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const row = {
        id: newId(),
        item: parsed.data.item,
        details: parsed.data.details ?? null,
        spentOn: parsed.data.spentOn,
        amountCents: parsed.data.amountCents,
        addedBy: "You",
      };
      jobExpenses.push({ ...row, companyId: cid, jobId: j.id, gone: false });
      return ok(row, 201);
    }
    if (method === "PATCH" && !seg[2]) {
      const parsed = jobPatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const { lines, linesFrom, active, crew, ...rest } = parsed.data;
      for (const [key, v] of Object.entries(rest)) if (v !== undefined) (j as unknown as Record<string, unknown>)[key] = v;
      if (crew) j.crew = crew;
      if (active === false && j.active) {
        j.active = false;
        j.endsOn = j.endsOn && j.endsOn < TODAY ? j.endsOn : TODAY;
      } else if (active === true && !j.active) {
        j.active = true;
        j.endsOn = null;
      }
      if (lines) replaceLinesFrom(j, linesFrom ?? TODAY, lines);
      ctx.log("job.update");
      return ok(jobDetail(j));
    }
    if (method === "POST" && seg[2] === "notes") return addNote(ctx, "jobs", j.id, b);
  }

  if (seg[0] === "job-expenses" && seg[1]) {
    const e = jobExpenses.find((x) => x.id === seg[1] && x.companyId === cid);
    if (!e) return NOT_FOUND("expense");
    if (!gate("job.update")) return NOT_ALLOWED();
    const rowOf = ({ companyId: _c, jobId: _j, gone: _g, ...r }: typeof e): JobExpenseRow => r;
    if (method === "DELETE") return ((e.gone = true), ok(null, 204));
    if (method === "POST" && seg[2] === "restore") return ((e.gone = false), ok(rowOf(e)));
    if (method === "PATCH") {
      const parsed = jobExpensePatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      for (const [k, v] of Object.entries(parsed.data)) if (v !== undefined) (e as unknown as Record<string, unknown>)[k] = v;
      return ok(rowOf(e));
    }
  }

  // Checklists
  if (route === "GET checklists")
    return gate("job.read")
      ? ok(
          checklists
            .filter((c) => c.companyId === cid)
            .map(({ companyId: _c, ...c }) => ({
              ...c,
              photosNeeded: 0,
              items: c.items.map((i) => ({ ...i, room: i.room ?? null, must: false, repeat: null })),
            })),
        )
      : NOT_ALLOWED();
  if (route === "POST checklists") {
    if (!gate("job.update")) return NOT_ALLOWED();
    const parsed = checklistInput.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const c: Checklist = {
      companyId: cid,
      id: newId(),
      name: parsed.data.name,
      items: parsed.data.items.map((i) => ({ id: newId(), label: i.label, labelEs: i.labelEs ?? null, room: i.room ?? null })),
    };
    checklists.push(c);
    return ok(
      {
        id: c.id,
        name: c.name,
        photosNeeded: parsed.data.photosNeeded,
        items: c.items.map((i) => ({ ...i, room: i.room ?? null, must: false, repeat: null })),
      },
      201,
    );
  }

  // Visits
  if (route === "GET visits") {
    if (!gate("schedule.read")) return NOT_ALLOWED();
    const from = query.get("from") ?? "";
    const to = query.get("to") ?? "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || to < from)
      return fail(400, "bad_request", "Pick a start and end date.");
    const clientId = query.get("clientId");
    const jobId = query.get("jobId");
    const span = daysBetween(from, to);
    if (span > (clientId || jobId ? 400 : 62)) return fail(400, "bad_request", "Pick a shorter range. The most is 62 days.");
    const personId = query.get("personId");
    const unassigned = query.get("unassigned") === "true";
    const list = planned(cid, from, to, (j) => (!clientId || j.clientId === clientId) && (!jobId || j.id === jobId))
      .map(card)
      .filter((v) => !personId || v.crew.some((x) => x.id === personId))
      .filter((v) => !unassigned || v.crew.length === 0)
      .sort((a, z) => a.date.localeCompare(z.date) || (a.startTime ?? "99").localeCompare(z.startTime ?? "99"));
    const team = ctx.people.filter((x) => x.companyId === cid && x.active && x.role === "crew").map((x) => x.id);
    return ok({ from, to, today: TODAY, visits: list, people: chips(team).map((x) => ({ ...x, role: "crew" })) });
  }
  if (seg[0] === "visits" && seg[1]) {
    const f = findVisit(cid, seg[1]);
    if (!f) return NOT_FOUND("visit");
    const sub = seg[2];
    if (method === "GET" && !sub) return gate("visit.read") ? ok(detail(f)) : NOT_ALLOWED();
    if (sub === "schedule" || sub === "crew" || sub === "future") {
      if (!gate("schedule.update")) return NOT_ALLOWED();
    } else if (!gate("visit.update")) return NOT_ALLOWED();
    if (sub === "notes" && method === "POST") return addNote(ctx, "visits", touch(f).row.id, b);
    if (sub === "photos" && method === "POST") return addFile(ctx, "visits", touch(f).row.id, b);
    const t = touch(f);
    const row = t.row;
    if (sub === "schedule" && method === "PATCH") {
      const parsed = visitScheduleBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (row.status === "done") return fail(409, "visit_done", "This visit is done. Reopen it to move it.");
      row.date = parsed.data.date;
      if (parsed.data.startTime !== undefined) row.startTime = parsed.data.startTime;
      if (parsed.data.endTime !== undefined) row.endTime = parsed.data.endTime;
      ctx.log("visit.move");
    } else if (sub === "lines" && method === "PUT") {
      const parsed = visitLinesBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (parsed.data.scope === "future" && t.job.frequency !== "one-time") {
        replaceLinesFrom(t.job, row.originalDate, parsed.data.lines);
        row.lines = null;
      } else row.lines = toLines(parsed.data.lines, null);
      ctx.log("visit.lines");
    } else if (sub === "crew" && method === "PUT") {
      const parsed = visitCrewBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const team = new Set(ctx.people.filter((x) => x.companyId === cid).map((x) => x.id));
      if (parsed.data.crew.some((x) => !team.has(x))) return fail(400, "bad_request", "Pick people from this team.");
      if (parsed.data.scope === "future") {
        t.job.crew = parsed.data.crew;
        row.crew = null;
      } else row.crew = parsed.data.crew;
      ctx.log("visit.crew");
    } else if (sub === "future" && method === "POST") {
      // The preview keeps it simple: the job takes this visit's team, time and lines (the repeat stays as it is).
      const parsed = visitFutureBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (t.job.frequency === "one-time") return fail(409, "one_time", "This job has only this visit.");
      if (parsed.data.crew && row.crew) t.job.crew = row.crew;
      if (parsed.data.time && row.startTime) t.job.startTime = row.startTime;
      if (parsed.data.lines && row.lines) {
        replaceLinesFrom(
          t.job,
          row.originalDate,
          row.lines.map((l) => ({ ...l, frequency: "every" as const })),
        );
        row.lines = null;
      }
      ctx.log("visit.future");
    } else if (sub === "status" && method === "POST") {
      const parsed = visitStatusBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (row.status === "done") return fail(409, "visit_done", "This visit is done. Reopen it first.");
      row.status = parsed.data.status;
      ctx.log(`visit.${parsed.data.status}`);
    } else if (sub === "complete" && method === "POST") {
      const parsed = visitCompleteBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (row.timerStartedAt) {
        row.timerMinutes += Math.round((Date.now() - Date.parse(row.timerStartedAt)) / 60_000);
        row.timerStartedAt = null;
      }
      row.status = "done";
      row.completedAt = new Date().toISOString();
      row.completedBy = ctx.me.name;
      const got = parsed.data.crewCollected;
      row.crewCollected = got === "cash" || got === "check" || got === "company" ? got : null;
      ctx.log("visit.complete");
      const done = doneOf(row);
      if (done) visitHooks.done(done);
    } else if (sub === "reopen" && method === "POST") {
      row.status = "scheduled";
      row.completedAt = null;
      row.completedBy = null;
      row.crewCollected = null;
      visitHooks.reopened(row.id);
      ctx.log("visit.reopen");
    } else if (sub === "time" && method === "DELETE") {
      if (!row.timerMinutes && !row.timerStartedAt) return fail(409, "no_time", "This visit has no time to remove.");
      row.removedMinutes = row.timerMinutes;
      row.timerMinutes = 0;
      row.timerStartedAt = null;
      ctx.log("visit.time.remove");
    } else if (sub === "time" && seg[3] === "restore" && method === "POST") {
      if (row.removedMinutes === undefined) return fail(409, "nothing_removed", "There is no removed time to bring back.");
      row.timerMinutes += row.removedMinutes;
      delete row.removedMinutes;
    } else if (sub === "timer" && method === "POST") {
      const parsed = visitTimerBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (parsed.data.action === "start" && !row.timerStartedAt) {
        row.timerStartedAt = new Date().toISOString();
        if (row.status === "scheduled") row.status = "in_progress";
      } else if (parsed.data.action === "stop" && row.timerStartedAt) {
        row.timerMinutes += Math.max(1, Math.round((Date.now() - Date.parse(row.timerStartedAt)) / 60_000));
        row.timerStartedAt = null;
      }
    } else if (sub === "checklist" && method === "POST") {
      const parsed = checklistTickBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (parsed.data.done) row.checks[parsed.data.itemId] = ctx.me.name;
      else row.checks[parsed.data.itemId] = null;
    } else return null;
    const out = fresh(cid, row);
    return out ? ok(out) : NOT_FOUND("visit");
  }
  return null;
}

function newProperty(companyId: string, clientId: string, d: z.infer<typeof propertyInput>): Property {
  return {
    companyId,
    clientId,
    id: newId(),
    label: d.label ?? null,
    address: d.address,
    city: d.city ?? null,
    state: d.state ?? null,
    zip: d.zip ?? null,
    beds: d.beds ?? null,
    baths: d.baths ?? null,
    rooms: d.rooms ?? null,
    sqft: d.sqft ?? null,
    lawnSqft: d.lawnSqft ?? null,
    accessNotes: d.accessNotes ?? null,
    notes: d.notes ?? null,
  };
}

function addNote(ctx: WorkCtx, on: Note["on"], subjectId: string, b: unknown): Answer {
  const parsed = noteInput.safeParse(b);
  if (!parsed.success) return bad(parsed.error);
  const n: Note = {
    companyId: ctx.companyId,
    id: newId(),
    on,
    subjectId,
    ...parsed.data,
    authorName: ctx.me.name,
    createdAt: new Date().toISOString(),
    deleted: false,
  };
  notes.push(n);
  return ok(noteRow(n), 201);
}

/** Pictures added in the preview, as data links by file id, so Photos shows each one small. */
const filePictures = new Map<string, string>();
export const workFilePicture = (path: string): string | null =>
  path.startsWith("files/") ? (filePictures.get(path.slice(6)) ?? null) : null;

function addFile(ctx: WorkCtx, on: StoredFile["on"], subjectId: string, b: unknown): Answer {
  const parsed = fileUpload.safeParse(b);
  if (!parsed.success) return bad(parsed.error);
  const f: StoredFile = {
    companyId: ctx.companyId,
    on,
    subjectId,
    deleted: false,
    id: newId(),
    name: parsed.data.name,
    mime: parsed.data.mime,
    size: Math.round((parsed.data.dataBase64.length * 3) / 4),
    kind: on === "visits" ? "photo" : parsed.data.kind,
    createdAt: new Date().toISOString(),
  };
  files.push(f);
  if (f.mime.startsWith("image/")) filePictures.set(f.id, `data:${f.mime};base64,${parsed.data.dataBase64}`);
  const { companyId: _c, on: _o, subjectId: _s, deleted: _d, ...row } = f;
  return ok(row, 201);
}

/** A made-up dispute file for the preview: one cleaning, signed, done, paid, then disputed as "not good". */
function demoDisputeFile(clientId: string, name: string): DisputeFile {
  const inv = "00000000-0000-4000-8000-0000000d1500";
  const f: DisputeFile = {
    client: {
      id: clientId,
      name,
      email: null,
      phone: null,
      address: "4421 Magazine St, New Orleans",
      since: "2026-03-02T15:00:00Z",
      card: "Visa ending 4242",
      cardSavedAt: "2026-09-01T14:02:00Z",
      cardByPhone: false,
      cardTakenBy: null,
      autoChargeAgreedOn: null,
    },
    invoices: [{ id: inv, number: 1182, totalCents: 24500, paidAt: "2026-10-14T21:12:00Z", disputed: true }],
    invoice: {
      id: inv,
      number: 1182,
      issuedOn: "2026-10-14",
      sentAt: "2026-10-14T20:00:00Z",
      viewedAt: "2026-10-14T21:10:00Z",
      paidAt: "2026-10-14T21:12:00Z",
      totalCents: 24500,
      lines: [{ description: "Deep Clean, 3 bed, 2 bath", amountCents: 24500 }],
    },
    dispute: {
      id: "00000000-0000-4000-8000-0000000d1501",
      stripeDisputeId: "du_demo",
      invoiceId: inv,
      reason: "product_unacceptable",
      status: "needs_response",
      amountCents: 24500,
      dueBy: "2026-11-12T04:59:59Z",
      openedAt: "2026-10-30T12:00:00Z",
      closedAt: null,
      sentAt: null,
      sentByName: null,
      note: null,
    },
    signature: {
      quoteId: "00000000-0000-4000-8000-0000000d1502",
      quoteNumber: 1006,
      name,
      at: "2026-09-01T14:03:00Z",
      ip: "73.12.44.9",
      device: null,
      drawing: true,
      policies: policiesToSign(SETTINGS_DEFAULTS),
      cardWords: "Your card is saved and charged only after each visit.",
    },
    confirmedByHand: [],
    visits: [
      {
        id: "00000000-0000-4000-8000-0000000d1503",
        date: "2026-10-14",
        startTime: "09:00",
        status: "done",
        completedAt: "2026-10-14T19:41:00Z",
        minutes: 212,
        address: "4421 Magazine St, New Orleans",
        crew: [
          { name: "Kenia Lopez", clockIn: "2026-10-14T13:58:00Z", clockOut: "2026-10-14T19:45:00Z", inFeet: 42, outFeet: 61 },
        ],
        seenAt: null,
        photos: [],
        checklistDone: 38,
      },
    ],
    payments: [
      {
        at: "2026-10-14T21:12:00Z",
        amountCents: 24500,
        method: "card",
        card: "Visa ending 4242",
        status: "succeeded",
        chargeId: null,
        thisOne: true,
      },
    ],
    pastPaid: { count: 3, totalCents: 52500, firstAt: "2026-04-02T18:00:00Z" },
    messages: [
      {
        at: "2026-10-14T13:40:00Z",
        from: "us",
        channel: "sms",
        body: "Kenia is on the way and will be there in about 20 minutes.",
      },
      { at: "2026-10-14T19:50:00Z", from: "client", channel: "sms", body: "Looks amazing, thank you!!" },
    ],
    complaints: [],
    ratings: [{ at: "2026-10-14T22:00:00Z", stars: 5, note: "Kenia was great" }],
    consent: [],
    complaintHours: 24,
    summary: `${name} hired us for a deep clean. On Oct 14 our team did the visit and marked it done. ${name} never told us anything was wrong before this dispute.`,
    gaps: ["No photos from the visit."],
  };
  return f;
}
